// Traslados por Saldo — cálculo ML acotado a las bodegas seleccionadas más el
// saldo completo de los grupos (del clasificador agrupador) fuera del rango
// [menor, mayor] en el origen.
// Diseño: docs/superpowers/specs/2026-07-30-traslados-por-saldo-agrupado-design.md
import React, { useState, useCallback, Fragment } from "react";
import { Scale, AlertTriangle } from "lucide-react";
import {
  COLORS, apiFetch, inputStyle, labelStyle, btnPrimary, thStyle, tdStyle, mono,
  cardStyle, tableWrap, CSVButton, PageHeader, LoadingSpinner, EmptyState, MetricCard,
} from "../shared/ui";
import MultiClassifierFilter from "../shared/MultiClassifierFilter";
import BodegaMultiSelect from "../shared/BodegaMultiSelect";

// Colores del badge de razón: las tres se distinguen entre sí porque el eje
// de esta pantalla es bajo-vs-alto. saldo_bajo usa danger (urgencia, falta
// stock) y saldo_alto usa warning (excedente); alta_rotacion_destino viene
// del cálculo base por ML, no del criterio de saldo, y usa success.
const RAZON_ESTILO = {
  alta_rotacion_destino: { bg: COLORS.successSoft, fg: COLORS.success },
  saldo_bajo: { bg: COLORS.dangerSoft, fg: COLORS.danger },
  saldo_alto: { bg: COLORS.warningSoft, fg: COLORS.warning },
};
const RAZON_ESTILO_DEFAULT = { bg: COLORS.surfaceHover, fg: COLORS.textMuted };

function TrasladosPorSaldoPage({ token }) {
  const [clas, setClas] = useState([]);
  const [origen, setOrigen] = useState([]);
  const [destino, setDestino] = useState([]);
  const [menor, setMenor] = useState("0");
  const [mayor, setMayor] = useState("10000");
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const onClasChange = useCallback((rows) => setClas(rows), []);

  // Motivo por el que no se puede calcular, o null si los valores son válidos.
  // Un campo vacío no vale 0: mandar mayor=0 haría que "saldo >= 0" marcara
  // todo el inventario de los clasificadores.
  const motivoInvalido = (() => {
    if (menor.trim() === "" || mayor.trim() === "")
      return "Complete los valores de Menor y Mayor";
    const nMenor = Number(menor);
    const nMayor = Number(mayor);
    if (!Number.isFinite(nMenor) || !Number.isFinite(nMayor))
      return "Menor y Mayor deben ser números";
    if (nMenor < 0) return "El valor de Menor no puede ser negativo";
    if (nMayor <= 0) return "El valor de Mayor debe ser mayor que cero";
    if (nMayor <= nMenor)
      return "El valor de Mayor debe ser estrictamente mayor que el de Menor";
    return null;
  })();

  const calcular = async () => {
    if (cargando || motivoInvalido) return;  // guard in-flight: el cálculo puede tardar
    setError(null);
    const params = new URLSearchParams();
    clas.forEach(r => params.append(
      "mclas", r.id_tipo ? `${r.id_clasificador}:${r.id_tipo}` : r.id_clasificador));
    origen.forEach(b => params.append("bodegas_origen", b));
    destino.forEach(b => params.append("bodegas_destino", b));
    params.append("menor", menor.trim());
    params.append("mayor", mayor.trim());

    setCargando(true);
    try {
      const res = await apiFetch(
        `/ml/suggest-transfers-por-saldo?${params}`, { method: "POST" }, token);
      setResultado(res);
    } catch (e) {
      setError(e.message || "No se pudo completar el cálculo");
      setResultado(null);
    } finally {
      setCargando(false);
    }
  };

  // Agrupar por par origen→destino conservando un orden estable
  const grupos = [];
  if (resultado?.data?.length) {
    const mapa = new Map();
    for (const fila of resultado.data) {
      const clave = `${fila.bodega_origen} → ${fila.bodega_destino}`;
      if (!mapa.has(clave)) mapa.set(clave, []);
      mapa.get(clave).push(fila);
    }
    for (const clave of [...mapa.keys()].sort()) {
      grupos.push([clave, mapa.get(clave).sort((a, b) =>
        (a.id_grupo || "").localeCompare(b.id_grupo || "") ||
        a.id_articulo.localeCompare(b.id_articulo))]);
    }
  }

  return (
    <div>
      <PageHeader
        title="Traslados por Saldo"
        subtitle="Cálculo ML sobre las bodegas seleccionadas, más el saldo completo de los grupos del clasificador agrupador fuera del rango"
      />

      <div style={{ ...cardStyle, padding: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ minWidth: 380 }}>
            <MultiClassifierFilter
              token={token} onChange={onClasChange}
              etiquetas={{ primera: "Agrupar por", resto: "Filtrar por (Y)" }}
            />
            <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>
              El orden importa: el primer clasificador agrupa el saldo; los
              demás filtran, y un artículo entra solo si cumple todos.
            </div>
          </div>
          <BodegaMultiSelect
            token={token} label="Bodegas que envían"
            seleccion={origen} onChange={setOrigen}
          />
          <BodegaMultiSelect
            token={token} label="Bodegas que reciben"
            seleccion={destino} onChange={setDestino}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              <label style={labelStyle}>Menor</label>
              <input
                type="number" min="0" value={menor}
                onChange={e => setMenor(e.target.value)}
                style={{ ...inputStyle, width: 110 }}
              />
            </div>
            <div>
              <label style={labelStyle}>Mayor</label>
              <input
                type="number" min="0" value={mayor}
                onChange={e => setMayor(e.target.value)}
                style={{ ...inputStyle, width: 110 }}
              />
            </div>
          </div>
          <button
            onClick={calcular} disabled={cargando || !!motivoInvalido}
            title={motivoInvalido || ""}
            style={{ ...btnPrimary, alignSelf: "flex-end",
                     opacity: (cargando || motivoInvalido) ? 0.6 : 1,
                     cursor: (cargando || motivoInvalido) ? "default" : "pointer" }}
          >
            <Scale size={14} style={{ marginRight: 6 }} />
            {cargando ? "Calculando…" : "Calcular"}
          </button>
        </div>

        {motivoInvalido ? (
          <div style={{ marginTop: 10, fontSize: 12, color: COLORS.danger,
                        display: "flex", gap: 6, alignItems: "center" }}>
            <AlertTriangle size={14} />{motivoInvalido}
          </div>
        ) : (
          <div style={{ marginTop: 10, fontSize: 12, color: COLORS.textDim }}>
            Se suma el saldo de cada grupo del clasificador agrupador en la
            bodega que envía; si el total es menor o igual a {menor} o mayor o
            igual a {mayor}, el grupo completo se traslada a una sola bodega.
          </div>
        )}
      </div>

      {error && (
        <div style={{
          ...cardStyle, padding: 12, marginBottom: 20, display: "flex", gap: 8,
          alignItems: "center", background: COLORS.dangerSoft,
          border: `1px solid ${COLORS.dangerLight}`, color: COLORS.danger, fontSize: 13
        }}>
          <AlertTriangle size={16} />{error}
        </div>
      )}

      {cargando && <LoadingSpinner />}

      {!cargando && resultado && (
        resultado.total_sugerencias === 0 ? (
          <EmptyState message="Ningún artículo cumplió las condiciones con estos filtros" />
        ) : (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <MetricCard icon={Scale} label="Total de traslados"
                value={resultado.total_sugerencias} />
              <MetricCard icon={Scale} label="Por rotación (ML)"
                value={resultado.total_base} color={COLORS.textMuted} />
              <MetricCard icon={Scale} label="Por saldo"
                value={resultado.total_por_saldo}
                color={COLORS.warning} />
            </div>

            <div style={{ marginBottom: 10 }}>
              <CSVButton data={resultado.data} filename="traslados_por_saldo"
                label="Descargar CSV" />
            </div>

            <div style={tableWrap}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Grupo</th>
                    <th style={thStyle}>Artículo</th>
                    <th style={thStyle}>Descripción</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Cantidad</th>
                    <th style={{ ...thStyle, textAlign: "right" }} title="Saldo actual del ERP en la bodega que envía (antes del traslado)">Saldo Origen</th>
                    <th style={{ ...thStyle, textAlign: "right" }} title="Saldo actual del ERP en la bodega que recibe (antes del traslado)">Saldo Destino</th>
                    <th style={thStyle}>Razón</th>
                    <th style={thStyle}>Origen del cálculo</th>
                  </tr>
                </thead>
                <tbody>
                  {grupos.map(([par, filas]) => (
                    <Fragment key={par}>
                      <tr>
                        <td colSpan={8} style={{
                          ...tdStyle, background: COLORS.primarySoft, fontWeight: 700,
                          color: COLORS.primary, fontSize: 12
                        }}>
                          {par} · {filas.length} artículo{filas.length === 1 ? "" : "s"}
                        </td>
                      </tr>
                      {filas.map((f, i) => (
                        <tr key={`${par}-${f.id_articulo}-${i}`}
                            style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                          <td style={{ ...tdStyle, ...mono, fontSize: 12 }}>
                            {f.id_grupo || "—"}
                          </td>
                          <td style={{ ...tdStyle, ...mono }}>{f.id_articulo}</td>
                          <td style={tdStyle}>{f.descripcion_articulo}</td>
                          <td style={{ ...tdStyle, ...mono, textAlign: "right", fontWeight: 700 }}>
                            {Number(f.cantidad_sugerida).toLocaleString()}
                          </td>
                          <td style={{ ...tdStyle, ...mono, textAlign: "right" }}>
                            {f.saldo_origen != null ? Number(f.saldo_origen).toLocaleString() : "—"}
                          </td>
                          <td style={{ ...tdStyle, ...mono, textAlign: "right" }}>
                            {f.saldo_destino != null ? Number(f.saldo_destino).toLocaleString() : "—"}
                          </td>
                          <td style={{ ...tdStyle, fontSize: 12 }}>
                            {f.razon && (() => {
                              const estilo = RAZON_ESTILO[f.razon] || RAZON_ESTILO_DEFAULT;
                              return (
                                <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11,
                                               background: estilo.bg, color: estilo.fg }}>
                                  {f.razon.replace(/_/g, " ")}
                                </span>
                              );
                            })()}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              padding: "2px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                              background: f.origen_calculo === "saldo"
                                ? COLORS.warningSoft : COLORS.surfaceHover,
                              color: f.origen_calculo === "saldo"
                                ? COLORS.warning : COLORS.textMuted
                            }}>
                              {f.origen_calculo === "saldo" ? "Saldo" : "Base"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 12, fontSize: 12, color: COLORS.textDim }}>
              Se guardaron como sugeridos los{" "}
              <strong>{resultado.total_guardados}</strong> traslados de esta
              corrida —los de rotación y los de saldo— y se gestionan desde la
              pantalla Traslados. Ojo: las líneas por rotación también las
              sugiere el cálculo de traslados normal, así que el mismo
              movimiento puede aparecer dos veces si se calcula en ambas
              pantallas.
            </div>
          </>
        )
      )}
    </div>
  );
}

export default TrasladosPorSaldoPage;
